import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, UserPlus } from 'lucide-react';
import { axiosInstance } from '@/utils/axiosConfig';
import { authStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InviteMember } from './InviteMember';
import { cn } from '@/lib/utils';
import { showToast } from '@/utils/toast';

interface OrganizationMember {
  id: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  role: {
    id: string;
    name: string;
  };
  joinedAt: string;
}

interface OrganizationMembersProps {
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
}

interface Role {
  id: string;
  name: string;
}

export const OrganizationMembers = ({
  loading,
  error,
  setError,
}: OrganizationMembersProps) => {
  const { t } = useTranslation();
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<OrganizationMember | null>(null);
  const [roleChangeConfirm, setRoleChangeConfirm] = useState<{
    member: OrganizationMember;
    newRole: Role;
  } | null>(null);

  // Define fetchMembers with useCallback to avoid dependency cycles
  const fetchMembers = useCallback(async () => {
    try {
      setLocalLoading(true);
      const orgId = authStore.currentOrganization?.id;
      if (!orgId) return;

      const response = await axiosInstance.get(`/organizations/${orgId}/members`);
      setMembers(response.data.members);
      setRoles(response.data.roles);
      setError(null);
    } catch (error) {
      console.error('Error fetching members:', error);
      const axiosError = error as AxiosError<{message: string}>;
      showToast.error({
        description: axiosError.response?.data?.message || t('organizations.failedToLoadMembers')
      });
    } finally {
      setLocalLoading(false);
    }
  }, [setError, t]);
  
  // Fetch members when component mounts
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    
    try {
      setLocalLoading(true);
      const orgId = authStore.currentOrganization?.id;
      if (!orgId) return;

      await axiosInstance.delete(`/organizations/${orgId}/members/${memberToRemove.id}`);
      await fetchMembers(); // Refresh the list
      showToast.success({
        description: t('organizations.memberRemoved')
      });
    } catch (error) {
      console.error('Error removing member:', error);
      const axiosError = error as AxiosError<{message: string}>;
      showToast.error({
        description: axiosError.response?.data?.message || t('organizations.failedToRemoveMember')
      });
    } finally {
      setLocalLoading(false);
      setMemberToRemove(null);
    }
  };

  // Add a function to check permissions
  const canManageMembers = (userRole?: string) => {
    return userRole === 'OWNER' || userRole === 'ADMIN';
  };

  // Add role color mapping function at the top of the component
  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'OWNER':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'ADMIN':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'MEMBER':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleRoleChange = async () => {
    if (!roleChangeConfirm) return;
    
    try {
      setLocalLoading(true);
      const { member, newRole } = roleChangeConfirm;
      
      await axiosInstance.patch(
        `/organizations/${authStore.currentOrganization?.id}/members/${member.id}/role`,
        { newRoleId: newRole.id }
      );

      await fetchMembers(); // Refresh the list
      showToast.success({
        description: t('organizations.memberRoleUpdated')
      });
    } catch (error) {
      console.error('Error updating role:', error);
      const axiosError = error as AxiosError<{message: string}>;
      showToast.error({
        description: axiosError.response?.data?.message || t('organizations.failedToUpdateRole')
      });
    } finally {
      setLocalLoading(false);
      setRoleChangeConfirm(null);
    }
  };

  if (loading || localLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
        <h2 className="text-xl font-semibold">{t('organizations.members')}</h2>
        {authStore.currentOrganization && 
         canManageMembers(authStore.currentOrganization.role) && (
          <InviteMember
            organizationId={authStore.currentOrganization.id}
            organizationName={authStore.currentOrganization.name}
            onInviteSent={fetchMembers}
          />
        )}
      </div>

      <Card className="p-3 md:p-6 overflow-x-auto">
        <div className="space-y-4 min-w-[300px]">
          {members.length === 0 ? (
            <p className="text-gray-600">{t('organizations.noMembers')}</p>
          ) : (
            <div className="divide-y">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between py-4 gap-2 first:pt-0 last:pb-0"
                >
                  <div className="space-y-1">
                    <p className="font-medium break-words">{member.user.name}</p>
                    <p className="text-sm text-gray-600 break-words">{member.user.email}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
                    <Badge
                      variant={member.role.name === 'OWNER' ? 'default' : 'secondary'}
                      className={cn(
                        getRoleBadgeVariant(member.role.name),
                        canManageMembers(authStore.currentOrganization?.role) ? 'cursor-pointer' : '',
                        'whitespace-nowrap'
                      )}
                      onClick={() => {
                        if (!canManageMembers(authStore.currentOrganization?.role) || member.role.name === 'OWNER') return;
                        
                        const newRoleName = member.role.name === 'MEMBER' ? 'ADMIN' : 'MEMBER';
                        const newRole = roles.find(r => r.name === newRoleName);
                        
                        if (newRole) {
                          setRoleChangeConfirm({ member, newRole });
                        }
                      }}
                    >
                      {member.role.name}
                    </Badge>
                    {member.user.id !== authStore.user?.id && 
                     canManageMembers(authStore.currentOrganization?.role) &&
                     (member.role.name !== 'ADMIN' || authStore.currentOrganization?.role === 'OWNER') &&
                     member.role.name !== 'OWNER' && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setMemberToRemove(member)}
                        className="whitespace-nowrap"
                      >
                        {t('common.remove')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <AlertDialog 
        open={!!memberToRemove} 
        onOpenChange={() => setMemberToRemove(null)}
      >
        <AlertDialogContent className="sm:max-w-md max-w-[90vw]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('organizations.removeMember')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('organizations.removeMemberConfirmation', { name: memberToRemove?.user.name })}
              {t('organizations.actionCannotBeUndone')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 w-full sm:w-auto"
            >
              {localLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('organizations.removing')}
                </>
              ) : (
                t('organizations.removeMember')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog 
        open={!!roleChangeConfirm} 
        onOpenChange={() => setRoleChangeConfirm(null)}
      >
        <AlertDialogContent className="sm:max-w-md max-w-[90vw]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('organizations.changeMemberRole')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('organizations.changeRoleConfirmation', { 
                name: roleChangeConfirm?.member.user.name,
                oldRole: roleChangeConfirm?.member.role.name,
                newRole: roleChangeConfirm?.newRole.name
              })}
              {t('organizations.actionCanBeReversed')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRoleChange}
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full sm:w-auto"
            >
              {localLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.updating')}
                </>
              ) : (
                t('organizations.changeRole')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}; 